// src/pages/league-dashboard/live-draft/components/TradeResponseModal.js
// Modal for reviewing and responding to incoming trade proposals
import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, ArrowLeftRight, Check, XCircle, User } from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';

export const TradeResponseModal = React.memo(({
  isOpen,
  onClose,
  trade,
  fromTeam,
  myTeam,
  allPicks,
  onAccept,
  onReject,
  isResponding = false
}) => {
  // Get pick details for display
  const getPickDetails = useMemo(() => {
    if (!allPicks) return { offering: [], requesting: [] };

    const offering = (trade?.offering_picks || [])
      .map(pickId => allPicks.find(p => p.pick_id === pickId))
      .filter(Boolean)
      .sort((a, b) => a.overall_pick_number - b.overall_pick_number);

    const requesting = (trade?.requesting_picks || [])
      .map(pickId => allPicks.find(p => p.pick_id === pickId))
      .filter(Boolean)
      .sort((a, b) => a.overall_pick_number - b.overall_pick_number);

    return { offering, requesting };
  }, [trade, allPicks]);

  // Handle accept
  const handleAccept = () => {
    onAccept(trade.trade_id);
  };

  // Handle reject
  const handleReject = () => {
    onReject(trade.trade_id);
    onClose();
  };

  if (!isOpen || !trade) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-lg ${dynastyTheme.components.card.base} p-6 m-4`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <ArrowLeftRight className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
            </div>
            <div>
              <h2 className={dynastyTheme.components.heading.h3}>Trade Proposal</h2>
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                From {fromTeam?.team_name || 'Unknown Team'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Trade details */}
        <div className="space-y-4 mb-6">
          {/* What they're offering (you get) */}
          <div className={`${dynastyTheme.components.card.base} p-4`}>
            <h4 className="text-sm font-bold mb-3 text-green-400 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              You Receive
            </h4>
            {getPickDetails.offering.length === 0 && (trade?.offering_players || []).length === 0 ? (
              <p className={`text-sm italic ${dynastyTheme.classes.text.neutralLight}`}>
                Nothing
              </p>
            ) : (
              <div className="space-y-2">
                {getPickDetails.offering.map(pick => (
                  <div
                    key={pick.pick_id}
                    className="flex items-center gap-3 p-2 bg-green-500/10 rounded border border-green-500/30"
                  >
                    <div className={`font-mono text-lg font-bold ${dynastyTheme.classes.text.primary}`}>
                      #{pick.overall_pick_number}
                    </div>
                    <div>
                      <span className={`text-sm ${dynastyTheme.classes.text.white}`}>
                        Round {pick.round_number || pick.round}, Pick {pick.round_pick_number || pick.pick_in_round}
                      </span>
                    </div>
                  </div>
                ))}
                {(trade?.offering_players || []).map(player => (
                  <div
                    key={typeof player === 'string' ? player : player.id}
                    className="flex items-center gap-3 p-2 bg-green-500/10 rounded border border-green-500/30"
                  >
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-green-400" />
                    </div>
                    <div>
                      <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                        {typeof player === 'string' ? player : player.full_name}
                      </span>
                      {player.position && (
                        <span className={`text-xs ml-2 ${dynastyTheme.classes.text.neutralLight}`}>
                          {player.position}
                          {player.salary != null ? ` — $${player.salary}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Divider with arrow */}
          <div className="flex items-center justify-center">
            <div className="flex-1 h-px bg-gray-700"></div>
            <div className="px-4">
              <ArrowLeftRight className="w-5 h-5 text-gray-500 rotate-90" />
            </div>
            <div className="flex-1 h-px bg-gray-700"></div>
          </div>

          {/* What they're requesting (you give) */}
          <div className={`${dynastyTheme.components.card.base} p-4`}>
            <h4 className="text-sm font-bold mb-3 text-yellow-400 flex items-center gap-2">
              <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
              You Give Up
            </h4>
            {getPickDetails.requesting.length === 0 && (trade?.requesting_players || []).length === 0 ? (
              <p className={`text-sm italic ${dynastyTheme.classes.text.neutralLight}`}>
                Nothing
              </p>
            ) : (
              <div className="space-y-2">
                {getPickDetails.requesting.map(pick => (
                  <div
                    key={pick.pick_id}
                    className="flex items-center gap-3 p-2 bg-yellow-500/10 rounded border border-yellow-500/30"
                  >
                    <div className={`font-mono text-lg font-bold ${dynastyTheme.classes.text.primary}`}>
                      #{pick.overall_pick_number}
                    </div>
                    <div>
                      <span className={`text-sm ${dynastyTheme.classes.text.white}`}>
                        Round {pick.round_number || pick.round}, Pick {pick.round_pick_number || pick.pick_in_round}
                      </span>
                    </div>
                  </div>
                ))}
                {(trade?.requesting_players || []).map(player => (
                  <div
                    key={typeof player === 'string' ? player : player.id}
                    className="flex items-center gap-3 p-2 bg-yellow-500/10 rounded border border-yellow-500/30"
                  >
                    <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div>
                      <span className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                        {typeof player === 'string' ? player : player.full_name}
                      </span>
                      {player.position && (
                        <span className={`text-xs ml-2 ${dynastyTheme.classes.text.neutralLight}`}>
                          {player.position}
                          {player.salary != null ? ` — $${player.salary}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleReject}
            disabled={isResponding}
            className={`flex-1 px-4 py-3 rounded font-medium flex items-center justify-center gap-2 transition-colors
              ${isResponding
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50'
              }`}
          >
            <XCircle className="w-5 h-5" />
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={isResponding}
            className={`flex-1 px-4 py-3 rounded font-medium flex items-center justify-center gap-2 transition-colors
              ${isResponding
                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-400 text-black'
              }`}
          >
            {isResponding ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent"></div>
                Processing...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Accept Trade
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
});

TradeResponseModal.displayName = 'TradeResponseModal';

export default TradeResponseModal;
